/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useCallback, useEffect, useMemo, useState} from "react";
import {usePostRequest} from "../../../../../../api/helper";
import {fetchDatasetDataEndpoint} from "../../../../../pages/CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/RightPanelComponents/constants";
import {v4 as uuidv4} from "uuid";
import toast from "react-hot-toast";
import ArgosButton from "../../../../Buttons/ArgosButton";
import {ObjectPropertiesData, PropertiesData} from "../../../../../pages/CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/RightPanelComponents/RightPanelCoverageRequirements";
import {KEY_NAME_DRIVING_SCENARIO_PROPERTIES, KEY_NAME_REDLINE_TYPE} from "../../../../../pages/CoverageRequirements/ItemDetails/constants";
import ADAS_PROGRAMS_CONSTANTS from "../../../constants";
import ArgosFlexbox from "../../../../Containers/ArgosFlexbox";
import {Typography} from "@mui/material";
import Loader from "../../../../Loader/Loader";
import {AnalysisResults} from "../../../../../pages/CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/RightPanelComponents/AnalysisResults";
import { defaultXLabel, defaultYLabel } from "../../../../../pages/CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/RightPanelComponents/AnalysisResultTab/constants";
import { List } from "lodash"

const SelectObjects = (props: any) => {
  const {selectedDataset} = props
  const {selectedObjects, setSelectedObjects} = props
  const datasetId = selectedDataset?.id

  const [allObjects, setAllObjects] = useState<any>()  // Map of objectId to object data for all objects in the dataset

  useEffect(() => {
    fetchObjectsFromRedlineDataset()
    fetchDataForDataset(false, true)
  }, [datasetId]);

  /**
   * Fetch redline data for graph
   */
  const fetchDatasetDataHandle = usePostRequest(fetchDatasetDataEndpoint)
  const [isLoadingDatasetData, setIsLoadingDatasetData] = useState(false)
  const [datasetData, setDatasetData] = useState<any>(null)
  const [isFetchingRaw, setIsFetchingRaw] = useState(false)
  const [errorFetchingRaw, setErrorFetchingRaw] = useState(false)
  const setLoadingState = (isLoading: boolean, fetchRaw: boolean, showMainLoader: boolean) => {
    if (fetchRaw) {
      setIsFetchingRaw(isLoading)
    }
    if (showMainLoader) {
      setIsLoadingDatasetData(isLoading)
    }
  }

  const [loadRawDatasetsData, setLoadRawDatasetsData] = useState<any>(null)

  /**
   * Fetches data for a dataset based on the provided parameters.
   *
   * @param fetchRaw - True if fetching raw data, otherwise only fetch redline.
   * @param showMainLoader - True if showing Loader over the entire right panel page.
   * @param doeValues - Optional list of doe column data to show in graph tooltip when hovering over a point.
   * @param x_axis - Selected x-axis column name.
   * @param y_axis - Selected y-axis column name.
   * @param objectIds - Data will be filtered based on list of object ids.
   *
   * @returns An AbortController instance if a request is made, otherwise null.
   */
  const fetchDataForDataset = (fetchRaw: boolean, showMainLoader: boolean,
    doeValues: List<String> = [], x_axis: string = defaultXLabel.variable_name,
    y_axis: string = defaultYLabel.variable_name, objectIds: List<String> = []): any => {
    if (datasetId == null || !(datasetId?.length > 0)) return null
    let body: any = {"selectedDatasetId": datasetId, fetchRaw}

    const loadOfRawDatasetsId = uuidv4();
    setLoadRawDatasetsData({loadOfRawDatasetsId})
    if (fetchRaw) {
      body["loadOfRawDatasetsId"] = loadOfRawDatasetsId
    }
    if (objectIds.length > 0) {
      body["mergedObjectsToPlot"] = objectIds
    }

    setLoadingState(true, fetchRaw, showMainLoader)
    setErrorFetchingRaw(false)

    return fetchDatasetDataHandle(body)
      .then((data: any) => {
        if ("error" in data) {

          toast.error((t) => (
              <span>
             {data["error"]}
                <ArgosButton
                  variant="text"
                  //size="small"
                  fontSize={11.5}
                  onClick={() => toast.remove(t.id)}>Dismiss
             </ArgosButton>
            </span>
            ),
            {
              duration: Infinity,
              id: "error-id"
            })

          setErrorFetchingRaw(true)
        } else {
          setDatasetData(data)
          setErrorFetchingRaw(false)
        }
      }).catch((e: any) => {
        console.log(e)
        toast.error("Error loading dataset data")
        setErrorFetchingRaw(true)
      }).finally(() => {
        setLoadingState(false, fetchRaw, showMainLoader)
      })
  }
  const doeTableData = datasetData?.data?.["doe_table"]
  const mapObjectsToPropertiesData = (objects: any): PropertiesData[] | any => {
    if (!objects) return []
    const _propertiesData: PropertiesData[] = []
    Object.keys(objects)?.forEach((objectKey: any) => {
      const object = objects[objectKey]
      const _object = {
        type: 'Object',
        category: object?.category,
        name: object?.name,
        property_link: object?.links?.[0]?.url,
        // fullData: {...object},
        objectId: objectKey,
        variantName: object?.variantName,
      }
      _propertiesData.push(_object)
    })
    return _propertiesData
  }

  const objectDataForPlotting: ObjectPropertiesData[] | any = useMemo(() => mapObjectsToPropertiesData(allObjects), [allObjects])

  /**
   * Fetch objects in redline and store them in allObjects state.
   */
  const fetchObjectsFromRedlineDatasetHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.fetchObjectsFromRedlineDatasetEndpoint)
  const [isLoadingObjects, setIsLoadingObjects] = useState(false)
  const fetchObjectsFromRedlineDataset = () => {
    if (!selectedDataset?.id) return
    setIsLoadingObjects(true)
    fetchObjectsFromRedlineDatasetHandle({datasetId: selectedDataset?.id}).then((_objects) => {
      if (!_objects) {
        toast.error('No objects found')
        return
      }
      setAllObjects(_objects)
    }).catch((err) => {
      toast.error('Error fetching objects from redline dataset')
    }).finally(() => {
      setIsLoadingObjects(false)
    })
  }

  const onPlotCheckboxesChange = useCallback((_data: any) => {
    if (!_data) return
    // data is list of names, filter all objects to find objects with that name
    const _selectedObjects: any = {}
    _data.forEach((objectId: string) => {
      if (!allObjects) return
      const _object = allObjects[objectId]
      if (_object) {
        _selectedObjects[_object?.id] = {name: _object?.name, type: _object?.type}
      }
    })
    setSelectedObjects(_selectedObjects)
  }, [allObjects])

  const preselectedObjects = useMemo(() => {
    return Object.keys(props.preselectedObjects)
  }, [props.preselectedObjects])

  return (
    <ArgosFlexbox vertical style={{height: '100%', width: '100%', padding: "20px"}} alignItems="center">
      <Typography>{selectedDataset?.name}</Typography>
      <Loader isLoading={isLoadingObjects}>
        <ArgosFlexbox style={{flex: 1}} alignItems="center">
          <AnalysisResults
            isLoading={isLoadingDatasetData}
            resultPlottingData={doeTableData}
            properties={objectDataForPlotting}
            datasetType={datasetData?.[KEY_NAME_REDLINE_TYPE]}
            outOfDate={datasetData?.['outOfDate']}
            onPlotCheckboxesChange={onPlotCheckboxesChange}
            preselectedObjects={preselectedObjects}
            fetchData={(fetchRaw: boolean, showMainLoader: boolean, doeValues?: List<String>, x_axis?: string, y_axis?: string, objectIds?: List<String>) => {
              fetchDataForDataset(fetchRaw, showMainLoader, doeValues ?? [], x_axis, y_axis, objectIds)
            }}
            isLoadingRawData={isFetchingRaw}
            errorFetchingRawData={errorFetchingRaw}
            loadRawDatasetsData={loadRawDatasetsData}
            isNewReportGraph={true}
          />
        </ArgosFlexbox>
      </Loader>
    </ArgosFlexbox>
  )
}


export default React.memo(SelectObjects);